import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { requestHandler } from '../../api';
import {
  CreatePaymentNotificationPayload,
  GetBankAccountResponse,
  GetInvoiceEncryptedResponse,
  GetInvoicesByEncryptionBusinessIdentifierResponse,
} from './types';

const PAYMENT_LINK_QUERY_KEY = 'paymentLink';

export const getInvoicesByEncryptionBusinessIdentifier = (
  encryptedCompanyIdentifier: string | undefined,
  encryptedBusinessIdentifier: string | undefined,
) =>
  useQuery({
    queryKey: [
      PAYMENT_LINK_QUERY_KEY,
      encryptedCompanyIdentifier,
      encryptedBusinessIdentifier,
    ],
    queryFn: async (): Promise<
      GetInvoicesByEncryptionBusinessIdentifierResponse | undefined
    > => {
      const response = await requestHandler({
        method: 'get',
        url: `public/paymentLink/${encryptedCompanyIdentifier}/${encryptedBusinessIdentifier}`,
      });
      if (response) return response.data;
    },
    retry: 1,
    enabled: !!encryptedCompanyIdentifier && !!encryptedBusinessIdentifier,
  });

export const getInvoiceEncrypted = (
  encryptedCompanyIdentifier: string | undefined,
  encryptedBusinessIdentifier: string | undefined,
  invoiceId: string | undefined,
) =>
  useQuery({
    queryKey: [
      PAYMENT_LINK_QUERY_KEY,
      'invoice',
      encryptedCompanyIdentifier,
      encryptedBusinessIdentifier,
      invoiceId,
    ],
    queryFn: async (): Promise<GetInvoiceEncryptedResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `public/paymentLink/invoice/${encryptedCompanyIdentifier}/${encryptedBusinessIdentifier}/${invoiceId}`,
      });
      if (response) return response.data;
    },
    retry: 1,
    enabled:
      !!encryptedCompanyIdentifier &&
      !!encryptedBusinessIdentifier &&
      !!invoiceId,
  });

export const createPaymentNotification = (
  maskedCompanyId: string | undefined,
  maskedBusinessIdentifier: string | undefined,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: CreatePaymentNotificationPayload,
    ): Promise<unknown> => {
      const response = await requestHandler({
        method: 'post',
        url: `public/paymentNotification/${maskedCompanyId}/${maskedBusinessIdentifier}`,
        data,
      });
      if (response) return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PAYMENT_LINK_QUERY_KEY],
      });
    },
  });
};

export const getBankAccount = (
  encryptedCompanyId: string | undefined,
  encryptedBusinessIdentifier: string | undefined,
) =>
  useQuery({
    queryKey: [
      PAYMENT_LINK_QUERY_KEY,
      'bankAccount',
      encryptedCompanyId,
      encryptedBusinessIdentifier,
    ],
    queryFn: async (): Promise<GetBankAccountResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `public/paymentLink/bankAccount/${encryptedCompanyId}/${encryptedBusinessIdentifier}`,
      });
      if (response) return response.data;
    },
    retry: 1,
    enabled: !!encryptedCompanyId && !!encryptedBusinessIdentifier,
  });
